/**
 * Pause the shiny button animations while the button is off screen.
 *
 * WHY THIS EXISTS
 * The shiny CTA runs `dtl-shine-rotate`, which animates the registered custom
 * property `--dtl-shine-angle`. That angle feeds a conic gradient used as a
 * background and as a mask. Custom property animations are NEVER composited,
 * registered with @property or not, so every frame the browser recomputes the
 * property, regenerates the conic gradient, rasterises it and rebuilds the
 * layer. On the element, on ::before and on ::after.
 *
 * Measured on the live store, 8 second idle window, reduced motion overridden:
 *     as shipped                 44% CPU, 1505ms style recalculation
 *     every shine paused         10% CPU,  187ms
 * so the shine was 77% of all idle CPU with nobody touching the page.
 *
 * The home page carries ten of these buttons and at most three are ever on
 * screen. An animation nobody can see costs exactly as much as one they can,
 * so the cheapest honest win is to stop the ones that are out of view. Nothing
 * visible changes: a button resumes before it scrolls in, thanks to the root
 * margin below.
 *
 * This does not make the visible ones cheap. That needs the sweep re-expressed
 * as a transform rotation, which is a change to the artwork and is being done
 * separately.
 */
const SELECTOR = 'a.button, button.button--shiny, .email-signup__button';
const IDLE_CLASS = 'dtl-shine-idle';
const SEEN = '__dtlShineObserved';

// Honour the user's own setting: with reduced motion the animations are off
// anyway, so there is nothing to pause and no reason to run an observer.
const reduced =
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!reduced && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle(IDLE_CLASS, !entry.isIntersecting);
      }
    },
    // Resume a little before it arrives, so the sweep is already running by
    // the time the button is actually visible.
    { rootMargin: '200px 0px' }
  );

  const observe = () => {
    for (const el of document.querySelectorAll(SELECTOR)) {
      if (el[SEEN]) continue;
      el[SEEN] = true;
      observer.observe(el);
    }
  };

  observe();

  // Sections re-render in the theme editor and when a block is added. No
  // MutationObserver on the body: this file exists to REMOVE per-frame work,
  // and watching every mutation on a page that already churns several times a
  // second would give some of it straight back.
  document.addEventListener('shopify:section:load', observe);
  document.addEventListener('shopify:block:select', observe);
}
